import React from 'react';
import trend1 from "../assets/trend_1.jpeg";
import {
    AppBar,
    Avatar,
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    Drawer,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Toolbar,
    Typography,
    useMediaQuery
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {Category} from '../helpers/categories';
import {useNavigate} from 'react-router-dom';
import {getUser, removeUser} from '../helpers/crypto';
import {getCartCount} from '../helpers/cart';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VerifiedIcon from '@mui/icons-material/Verified';
import BoltIcon from '@mui/icons-material/Bolt';
import ShieldIcon from '@mui/icons-material/Shield';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {Constants} from "../components/Constants";
import {iconNameToIcon} from "../components/iconNameToIcon";
import {GetAvatarNikFromName, UserTypeToColor} from "../helpers/UserTypeToColor";

const navLinks = ['Про нас', 'Каталог', 'Контакти'];
const navAnchors = ['#about','/catalog','#contacts'];

const navAdminLinks = ['Користувачі', 'Каталог', 'Замовлення'];
const navAdminAnchors = ['/admin/users','/catalog','/admin/orders'];

const advantages = [
    { icon: <VerifiedIcon />, title: 'Якісні запчастини', desc: 'Працюємо напряму з виробниками, гарантуємо якість кожної деталі' },
    { icon: <BoltIcon />, title: 'Швидка доставка', desc: 'Відправка протягом 24 годин, доставка по Україні за 1-3 дні' },
    { icon: <ShieldIcon />, title: 'Гарантія якості', desc: 'Якісні комплектуючі для тривалого використання' },
    { icon: <SupportAgentIcon />, title: 'Технічна підтримка', desc: 'Консультації спеціалістів з підбору та встановлення деталей' },
];

const stats = [
    { value: '10+', label: 'Років на ринку' },
    { value: '5 000+', label: 'Успішних відправлень' },
    { value: '1 200+', label: 'Задоволених клієнтів' },
    { value: '24/7', label: 'Підтримка клієнтів' },
];

const HomePage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [user, setUser] = React.useState<any>(null);
    const [cartCount, setCartCount] = React.useState(0);
    const [categories, setCategories] = React.useState<Category[]>([]);

    React.useEffect(() => {
        setUser(getUser());
        setCartCount(getCartCount());
        fetch(`${Constants.SERVER_URL}${Constants.GET_CATEGORIES_PREFIX}`)
            .then(r => r.json())
            .then(d => { if (d.success) setCategories(d.categories); })
            .catch(() => {});

        const onStorage = () => { setUser(getUser()); setCartCount(getCartCount()); };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const NavList = ({ links, anchors }:{links: string[],anchors: string[]}) => (
        <List>
            {links.map((link, i) => {
                const anchor = anchors[i];
                const isHash = anchor.includes("#");

                const handleClick = () => {
                    if (isHash) {
                        setDrawerOpen(false);
                    } else {
                        navigate(anchor);
                        setDrawerOpen(false);
                    }
                };

                return (
                    <ListItem key={link} disablePadding>
                        <ListItemButton
                            component="a"
                            href={isHash ? anchor : undefined}
                            onClick={handleClick}
                        >
                            <ListItemText primary={link} />
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>
    );

    const handleLogout = () => {
        removeUser();
        setUser(null);
    };

    return (
        <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
            {/* Header */}
            <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)'
                , borderBottom: '1px solid #e0e6ed' }}>
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ height: 70 }}>
                        <LocalHospitalIcon sx={{ color: '#0077CC', mr: 1, fontSize: 32 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e'
                            , flexGrow: isMobile ? 1 : 0, mr: 4 }}>
                            {Constants.APP_LABEL}
                        </Typography>

                        {!isMobile && (
                            <>
                                {(user && user.status === 'ADMIN') ? (
                                        <Box sx={{ display: 'flex', gap: 3, flexGrow: 1 }}>
                                            {navAdminLinks.map((link, i) => {
                                                const anchor = navAdminAnchors[i];
                                                const isHash = anchor.includes("#");

                                                const handleClick = () => {
                                                    if (!isHash) {
                                                        navigate(anchor);
                                                    }
                                                };

                                                return (
                                                    <Button
                                                        key={link}
                                                        href={isHash ? anchor : undefined}
                                                        onClick={isHash ? undefined : handleClick}
                                                        sx={{ color: '#555', textTransform: 'none', fontWeight: 500 }}
                                                    >
                                                        {link}
                                                    </Button>
                                                );
                                            })}
                                        </Box>
                                ):
                                    <Box sx={{ display: 'flex', gap: 3, flexGrow: 1 }}>
                                        {navLinks.map((link, i) => {
                                            const anchor = navAnchors[i];
                                            const isHash = anchor.includes("#");

                                            const handleClick = () => {
                                                if (!isHash) {
                                                    navigate(anchor);
                                                }
                                            };

                                            return (
                                                <Button
                                                    key={link}
                                                    href={isHash ? anchor : undefined}
                                                    onClick={isHash ? undefined : handleClick}
                                                    sx={{ color: '#555', textTransform: 'none', fontWeight: 500 }}
                                                >
                                                    {link}
                                                </Button>
                                            );
                                        })}
                                    </Box>
                                }
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                    {user ? (
                                        <>
                                            <IconButton onClick={() => navigate('/cart')}>
                                                <Badge badgeContent={cartCount > 0 ? cartCount : null} color="primary">
                                                    <ShoppingCartOutlinedIcon />
                                                </Badge>
                                            </IconButton>
                                                <Avatar sx={{ cursor: "pointer"
                                                    ,bgcolor: UserTypeToColor[user?.status ?? "REGULAR"] }}
                                                onClick={() => navigate('/profile')}
                                                >{GetAvatarNikFromName(user.name,user.last_name)}</Avatar>
                                            <Button variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}
                                                    onClick={handleLogout}>Вийти</Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}
                                                    onClick={() => navigate('/login')}>Увійти</Button>
                                            <Button variant="contained" sx={{ textTransform: 'none', borderRadius: 2 }}
                                                    onClick={() => navigate('/register')}>Реєстрація</Button>
                                        </>
                                    )}
                                </Box>
                            </>
                        )}

                        {isMobile && (
                            <>
                                {user && (
                                    <IconButton onClick={() => navigate('/cart')}>
                                        <Badge badgeContent={cartCount > 0 ? cartCount : null} color="primary">
                                            <ShoppingCartOutlinedIcon />
                                        </Badge>
                                    </IconButton>
                                )}
                                <IconButton onClick={() => setDrawerOpen(true)}>
                                    <MenuIcon />
                                </IconButton>
                            </>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 260, pt: 2 }}>
                    {user && user.status === 'ADMIN' ? (
                        <NavList links={navAdminLinks} anchors={navAdminAnchors} />
                    ) : (
                        <NavList links={navLinks} anchors={navAnchors} />
                    )}

                    <Divider />
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {user ? (
                            <>
                                    <Button fullWidth sx={{ textTransform: 'none', color: '#555', fontWeight: 500 }}
                                            onClick={() => { setDrawerOpen(false); navigate('/profile'); }}>
                                        {user.name} {user.last_name}
                                    </Button>
                                <Button variant="outlined" fullWidth startIcon={
                                    <Badge badgeContent={cartCount > 0 ? cartCount : null} color="primary">
                                        <ShoppingCartOutlinedIcon />
                                    </Badge>
                                } sx={{ textTransform: 'none', borderRadius: 2 }} onClick={() =>
                                { setDrawerOpen(false); navigate('/cart'); }}>
                                    Кошик
                                </Button>
                                <Button variant="outlined" fullWidth sx={{ textTransform: 'none', borderRadius: 2 }}
                                        onClick={() => { setDrawerOpen(false); handleLogout(); }}>Вийти</Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outlined" fullWidth sx={{ textTransform: 'none', borderRadius: 2 }}
                                        onClick={() => { setDrawerOpen(false); navigate('/login'); }}>
                                    Увійти
                                </Button>
                                <Button variant="contained" fullWidth sx={{ textTransform: 'none', borderRadius: 2 }}
                                        onClick={() => { setDrawerOpen(false); navigate('/register'); }}>
                                    Реєстрація
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Drawer>

            {/* Hero */}
            <Box id="about" sx={{ py: { xs: 6, md: 10 }, background: 'linear-gradient(135deg, #fff 0%, #e6f2ff 100%)' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} sx={{ alignItems: 'center' }}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Typography variant="h2" sx={{ fontWeight: 700,
                                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' }, lineHeight: 1.15, mb: 2.5 }}>
                                Комплектуючі для медичної техніки
                            </Typography>
                            <Typography sx={{ fontSize: { xs: 16, md: 18 }, color: '#555', mb: 4, maxWidth: 520,
                                lineHeight: 1.7, mx: { xs: 'auto', md: 0 } }}>
                                Ми пропонуємо надійні запчастини та комплектуючі для діагностичного
                                медичного обладнання. Співпрацюємо за оптовими цінами з магазинами медтехніки та
                                сервісними центрами. Швидка доставка по всій Україні.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center',
                                    md: 'flex-start' } }}>
                                <Button variant="contained" size="large" sx={{ textTransform: 'none', borderRadius: 2, px: 4 }}
                                        onClick={() => navigate('/catalog')}>
                                    Переглянути каталог
                                </Button>
                                <Button variant="outlined" size="large" sx={{ textTransform: 'none', borderRadius: 2, px: 4 }}>
                                    Зв'язатися з нами
                                </Button>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }} sx={{ textAlign: 'center' }}>
                            <Box sx={{ width: { xs: 200, md: 300 }, height: { xs: 200, md: 300 }, mx: 'auto' }}>
                                <img
                                    src={trend1}
                                    alt="trend"
                                    style={{
                                        borderRadius: "10%",
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        maskImage:
                                            "linear-gradient(to right, transparent, black 20%, black 80%, transparent)" +
                                            ",linear-gradient(to bottom, transparent, black 20%, black 80%, transparent)",
                                        maskRepeat: "no-repeat",
                                        maskComposite: "intersect",
                                        maskSize: "cover",
                                    }}
                                />

                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Stats */}
            <Box sx={{ py: 5, borderBottom: '1px solid #e0e6ed' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={3} sx={{ justifyContent: 'space-around' }}>
                        {stats.map((s) => (
                            <Grid key={s.label} sx={{ textAlign: 'center' }}>
                                <Typography sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 700, color: '#0077CC' }}>
                                    {s.value}
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: '#555' }}>{s.label}</Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Categories */}
            <Box id="catalog" sx={{ py: { xs: 6, md: 10 }, bgcolor: '#f7f9fc' }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 1.5 }}>
                        Категорії комплектуючих
                    </Typography>
                    <Typography sx={{ color: '#555', textAlign: 'center', mb: 6, maxWidth: 550, mx: 'auto' }}>
                        Оберіть потрібну категорію для пошуку необхідних деталей
                    </Typography>
                    <Grid container spacing={3}>
                        {categories.map((cat) => (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cat.title}>
                                <Card elevation={0}
                                      onClick={() => navigate(`/catalog?category=${cat.id}`)}
                                      sx={{
                                    border: '1px solid #e0e6ed', borderRadius: 3, height: '100%',
                                    transition: '0.25s', cursor: 'pointer',
                                    '&:hover': { boxShadow: '0 8px 30px rgba(0,0,0,0.1)', transform: 'translateY(-4px)'
                                        , borderColor: '#0077CC' }
                                }}>
                                    <CardContent sx={{ p: 3.5 }}>
                                        <Box sx={{ color: '#0077CC', mb: 2 }}>{iconNameToIcon[cat.iconame]}</Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: 18 }}>
                                            {cat.title}</Typography>
                                        <Typography sx={{ fontSize: 14, color: '#555', mb: 2 }}>
                                            {cat.description}</Typography>
                                        <Typography sx={{ fontSize: 14, color: '#0077CC', fontWeight: 500 }}>
                                            Детальніше →</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Advantages */}
            <Box id="advantages" sx={{ py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 6 }}>
                        Чому обирають нас
                    </Typography>
                    <Grid container spacing={3}>
                        {advantages.map((adv) => (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={adv.title}>
                                <Box sx={{ textAlign: 'center', p: { xs: 2, md: 3 } }}>
                                    <Box sx={{
                                        width: 56, height: 56, borderRadius: '50%', bgcolor: '#e6f2ff',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        mx: 'auto', mb: 2, color: '#0077CC'
                                    }}>
                                        {adv.icon}
                                    </Box>
                                    <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 17 }}>{adv.title}</Typography>
                                    <Typography sx={{ fontSize: 14, color: '#555', lineHeight: 1.6 }}>{adv.desc}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA */}
            <Box sx={{ py: { xs: 6, md: 10 }, background: 'linear-gradient(135deg, #0077CC 0%, #005fa3 100%)', color: '#fff' }}>
                <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
                        Не знайшли потрібну деталь?
                    </Typography>
                    <Typography sx={{ fontSize: 17, opacity: 0.9, mb: 4, maxWidth: 500, mx: 'auto' }}>
                        Надішліть запит — наші спеціалісти підберуть комплектуючі під ваше обладнання
                    </Typography>
                    <Button variant="contained" size="large" sx={{
                        textTransform: 'none', borderRadius: 2, px: 4,
                        bgcolor: '#fff', color: '#0077CC', '&:hover': { bgcolor: '#e6f2ff' }
                    }}>
                        Надіслати запит
                    </Button>
                </Container>
            </Box>

            {/* Footer */}
            <Box id="contacts" component="footer" sx={{ pt: 7, bgcolor: '#f7f9fc', borderTop: '1px solid #e0e6ed' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} sx={{ mb: 5 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <LocalHospitalIcon sx={{ color: '#0077CC' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: 20 }}>КОМПОНЕНТА</Typography>
                            </Box>
                            <Typography sx={{ fontSize: 14, color: '#555', lineHeight: 1.6 }}>
                                Комплектуючі для медичної техніки з доставкою по всій Україні
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <Typography sx={{ fontWeight: 600, mb: 2, fontSize: 15 }}>Навігація</Typography>
                            {user && user.status === 'ADMIN' ? (
                                navAdminLinks.map((link, i) => (
                                    <Typography key={link} component="a"
                                                href={navAdminAnchors[i].includes("#") ?
                                                    navAdminAnchors[i] :
                                                    undefined}
                                                onClick={navAdminAnchors[i]
                                                    .includes("#")
                                                    ? undefined :
                                                    () => navigate(navAdminAnchors[i])
                                                }
                                                sx={{ cursor: 'pointer',
                                                    display: 'block', fontSize: 14, color: '#555', textDecoration: 'none', mb: 1,
                                                    '&:hover': { color: '#0077CC' }
                                                }}>
                                        {link}
                                    </Typography>
                                ))
                            ) : (
                                navLinks.map((link, i) => (
                                        <Typography key={link} component="a"
                                                    href={navAnchors[i].includes("#") ?
                                                        navAnchors[i] :
                                                        undefined}
                                                    onClick={navAnchors[i]
                                                        .includes("#")
                                                        ? undefined :
                                                        () => navigate(navAnchors[i])
                                                    }
                                                    sx={{ cursor: 'pointer',
                                                        display: 'block', fontSize: 14, color: '#555', textDecoration: 'none', mb: 1,
                                                        '&:hover': { color: '#0077CC' }
                                                    }}>
                                            {link}
                                        </Typography>
                                    ))
                            )}
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography sx={{ fontWeight: 600, mb: 2, fontSize: 15 }}>Контакти</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PhoneIcon sx={{ fontSize: 16, color: '#555' }} />
                                <Typography sx={{ fontSize: 14, color: '#555' }}>+38 (044) 123-45-67</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <EmailIcon sx={{ fontSize: 16, color: '#555' }} />
                                <Typography sx={{ fontSize: 14, color: '#555' }}>info@componenta.in.ua</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationOnIcon sx={{ fontSize: 16, color: '#555' }} />
                                <Typography sx={{ fontSize: 14, color: '#555' }}>м. Київ, вул. Медична, 1</Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography sx={{ fontWeight: 600, mb: 2, fontSize: 15 }}>Графік роботи</Typography>
                            <Typography sx={{ fontSize: 14, color: '#555', mb: 0.5 }}>Пн-Пт: 9:00 — 18:00</Typography>
                            <Typography sx={{ fontSize: 14, color: '#555', mb: 0.5 }}>Сб: 10:00 — 15:00</Typography>
                            <Typography sx={{ fontSize: 14, color: '#555' }}>Нд: вихідний</Typography>
                        </Grid>
                    </Grid>
                    <Divider />
                    <Box sx={{ py: 2.5, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 13, color: '#555' }}>© 2026 Компонента. Усі права захищені.</Typography>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default HomePage;
